import PaymentStatus from "enums/PaymentStatus";

/** Inbox: payments waiting on a person or on funds. */
export const INBOX_STATUSES: PaymentStatus[] = [
  PaymentStatus.DRAFT,
  PaymentStatus.PENDING,
  PaymentStatus.INSUFFICIENT_FUNDS,
  PaymentStatus.PAYMENT_FAILED,
];

/** Processing: payments the system is moving. */
export const PROCESSING_STATUSES: PaymentStatus[] = [
  PaymentStatus.PENDING_PAYMENT,
  PaymentStatus.PROCESSING_PAYMENT,
];

/** History lists every payment, so it carries no status constraint. */
export const HISTORY_STATUSES: PaymentStatus[] = [];

/** The statuses each tab lists, in tab order. */
export const PAYMENT_TAB_STATUSES: readonly (readonly PaymentStatus[])[] = [
  INBOX_STATUSES,
  PROCESSING_STATUSES,
  HISTORY_STATUSES,
];

/**
 * The statuses a tab can show. A tab with no status list shows every payment,
 * so it offers every status, and one added to the enum later is offered by default.
 */
export const statusesOffered = (
  tabStatuses: readonly PaymentStatus[],
): PaymentStatus[] =>
  tabStatuses.length > 0 ? [...tabStatuses] : Object.values(PaymentStatus);

/** The picks this tab can show; the rest stay picked for a tab that shows them. */
export const statusPicksOnTab = (
  picked: readonly string[],
  tabStatuses: readonly PaymentStatus[],
): PaymentStatus[] =>
  statusesOffered(tabStatuses).filter((status) => picked.includes(status));

/**
 * The picks after the visible ones change. The filter only sees this tab's
 * picks, so the ones the tab does not offer are carried through, not replaced.
 */
export const applyStatusPicks = (
  previous: readonly string[],
  next: readonly string[],
  tabStatuses: readonly PaymentStatus[],
): string[] => {
  const offered: readonly string[] = statusesOffered(tabStatuses);
  return [...previous.filter((status) => !offered.includes(status)), ...next];
};

/**
 * The statuses the query asks for. The tab picks the working set and the picks
 * narrow inside it, so a pick the tab excludes changes nothing rather than
 * emptying the table. No picks means the tab's own list, and an empty list
 * reaches the server as "no constraint".
 */
export const statusesToQuery = (
  picked: readonly string[],
  tabStatuses: readonly PaymentStatus[],
): PaymentStatus[] => {
  const onTab = statusPicksOnTab(picked, tabStatuses);
  return onTab.length > 0 ? onTab : [...tabStatuses];
};
